import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Client, ReconnectionTimeMode, StompConfig, StompSubscription, TickerStrategy } from '@stomp/stompjs';
import { BehaviorSubject } from 'rxjs';
import { filter, pairwise, skip } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AESEncryptDecryptService } from './AESEncryptDecryptService.service';
/*Object.assign(global, { WebSocket }); */

export interface WebsocketMessage {
  channel : string;
  message : string;
}

@Injectable({
  providedIn: 'root'
})
export class WebsocketService {

  private messageListener: BehaviorSubject<WebsocketMessage | null> = new BehaviorSubject<WebsocketMessage | null>(null);
  messageListener$ = this.messageListener.asObservable().pipe(skip(1));

  private unblockDataDeleteSubject = new BehaviorSubject<boolean>(false);
  unblockDataDeleteSubject$ = this.unblockDataDeleteSubject.asObservable().pipe(skip(1));

  private unblockStrategyDataSubject = new BehaviorSubject<boolean>(false);
  unblockStrategyDataSubject$ = this.unblockStrategyDataSubject.asObservable().pipe(skip(1));

  private connectionState = new BehaviorSubject<boolean>(false);
  connectionState$ = this.connectionState.asObservable();
  reconnected$ = this.connectionState.pipe(pairwise(), filter(([before, now]) => !before && now));

  client : Client | null;
  isConnected : boolean = false;
  previouslyDisconnected : boolean = false;
  subscriptions : string[] = []; // canales deseados (fuente de verdad)
  private stompSubscriptions : { [channel : string] : StompSubscription } = {}; // handles vivos en la conexion actual

  constructor(
    private _AESEncryptDecryptService : AESEncryptDecryptService
  ) { }

  init() {
    if(this.client){
      if(!environment.production) console.log("ALREADY CONNECTED");
      return this.client;
    }

    this.client = new Client({
      brokerURL: environment.websocketServerUrl,
      beforeConnect: async (client) => {
        const token = localStorage.getItem(environment.authTokenKey);
        if (!token) throw new Error('WebSocket authentication token is not available');
          client.connectHeaders = {
            Authorization: `Bearer ${token}`
          };
      },
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      heartbeatStrategy: TickerStrategy.Worker,
      reconnectDelay: 1000,
      reconnectTimeMode: ReconnectionTimeMode.EXPONENTIAL,
      maxReconnectDelay: 30000,
      // TEMPORAL - diagnostico de heartbeat. Descomentar para ver todos los frames STOMP.
      //debug: (msg) => { if(!environment.production) console.log("STOMP DEBUG", msg); },
      onConnect: (frame) => {
        if(!environment.production) console.log("CONNECTED");
        if(!environment.production) console.log("HEART-BEAT NEGOCIADO:", frame.headers['heart-beat'], "| version:", frame.headers['version']);
        this.stompSubscriptions = {};          // los handles anteriores murieron con el socket
        this.isConnected = true;
        this.reconnectSubscriptions();
        this.connectionState.next(true);
      },
      onWebSocketClose: (evt) => {
        if(!environment.production) console.log("DISCONNECTED", evt.code, evt.reason);
        this.stompSubscriptions = {};
        this.isConnected = false;
        this.connectionState.next(false);
      },
      onStompError: frame => { 
        if(!environment.production) console.error("STOMP ERROR", frame.headers, frame.body);
      },
    });
    this.client.activate();
    return this.client;
  }

  subscribe(channel : string){
    if (this.subscriptions.indexOf(channel) == -1) this.subscriptions.push(channel);
    if (!this.client?.connected) return;                // se suscribira en onConnect
    if (this.stompSubscriptions[channel]) return;       // ya esta suscrito en esta conexion
    try {
      this.stompSubscriptions[channel] = this.client.subscribe(channel, message => {
        this.messageListener.next({ channel, message: this._AESEncryptDecryptService.decryptWebSocketService(message.body)});
      });
    } catch (err) {
      if(!environment.production) console.error(err);
    }
  }

  reconnectSubscriptions(){
    if(this.subscriptions.length){
      if(!environment.production) console.log("RECONNECT SUBSCRIPTIONS", this.subscriptions);
      this.subscriptions.forEach(channel => this.subscribe(channel));
    }
  }

  unsubscribe(channel : string){
    const index = this.subscriptions.indexOf(channel);
    if(index != -1) this.subscriptions.splice(index, 1);
    this.detach(channel);
  }

  unsubscribeAll(){
    Object.keys(this.stompSubscriptions).forEach(channel => this.detach(channel));
    this.subscriptions = [];
  }

  /* 
    Notas importantes para entender la logica
    Set: lo que hace es validar que en ese listado de ids no existan ids repetidos, si lo existe los elimina quedando con uno solo
    ids.filter: La validacion lo que hace es si primero evaluar el id como un boolean al agregar los '!!' lo que hace evaluar es si id tiene valores sera true, si es null o undefined sera false,
    con ello lo que hace el id is string es decir, si es true el resultado considera apartir de aqui en adelante que id siempre es un string, luego lo recorre y arma la estructura

  */
  reconcileSubscriptions(prefix : string, ids : (string | null | undefined)[]){
    const desiredChannels = new Set(ids.filter((id) : id is string => !!id).map(id => prefix + id));
    this.subscriptions.filter(channel => channel.startsWith(prefix) && !desiredChannels.has(channel)).forEach(channel => this.unsubscribe(channel));
    desiredChannels.forEach(channel => this.subscribe(channel));
  }

  refreshAuthentication(){
    const client = this.client;
    if (!client?.active) return;
    client.deactivate({ force: true }).then(() => {
      if (this.client === client && localStorage.getItem(environment.authTokenKey)) {
        client.activate();
      }
    });
  }

  private detach(channel : string){
    const sub = this.stompSubscriptions[channel];
    delete this.stompSubscriptions[channel];
    if (!sub || !this.client?.connected) return;
    try { 
      sub.unsubscribe(); 
    } catch (err) { 
      if(!environment.production) console.error(err); 
    }
  }

  destroy(){
    if(!this.client) return;
    try {
      this.client.deactivate({ force : true });
    } catch (err) {
      console.log(err);
    }
    this.client = null;
    this.subscriptions = [];
    this.stompSubscriptions = {};
    this.isConnected = false;
    this.connectionState.next(false);
  }


 /* init(){
  if(this.client){
    if(!environment.production) console.log("ALREADY CONNECTED");
    return this.client;
  }

  this.client = new Client({
    brokerURL: environment.websocketServerUrl,
    onConnect: () => {
      if(!environment.production) console.log("CONNECTED");
      this.isConnected = true;
      this.reconnectSubscriptions();
    },
    onDisconnect : () => {
      if(!environment.production) console.log("DISCONNECTED");
      this.isConnected = false;
      this.previouslyDisconnected = true;
    },
    heartbeatIncoming: 10000,
    heartbeatOutgoing: 20000,
    reconnectDelay: 1500,
  });
  this.client.activate();
  return this.client;
 } */

 /* subscribe(channel : string){
   try {
     if (this.subscriptions.indexOf(channel) == -1) this.subscriptions.push(channel);
     if (this.client) this.client.unsubscribe(channel);
     this.client?.subscribe(channel, message => {
       if (this.subscriptions.indexOf(channel) != -1) this.messageListener.next({
         channel,
         message: message.body
       })
     })
   }
   catch (err) {
     console.log(err);
   }
} */

  emitUnblockDataDeleteEvent(){
    this.unblockDataDeleteSubject.next(true);
  }

  emitUnblockStrategyDeleteEvent(){
    this.unblockStrategyDataSubject.next(true);
  }

 /* reconnectSubscriptions(){
  if(this.subscriptions.length){
    if(!environment.production) console.log("RECONNECT SUBSCRIPTIONS", this.subscriptions);
    this.subscriptions.map(x => {
      this.subscribe(x);
    })
  }
 } */

 /* unsubscribe(channel : string){
  try {
    let index = this.subscriptions.indexOf(channel);
    if(index != -1) this.subscriptions.splice(index, 1);
    if(this.client) this.client.unsubscribe(channel);
  } catch (error) {
    console.log(error);
  }
 } */

 /* unsubscribeAll(){
  if(this.client) this.subscriptions.map(channel => {
    this.client?.unsubscribe(channel);
  })
  this.subscriptions = [];
 } */

 /* destroy(){
  if(this.client){
    try{
      this.client.deactivate({force : true});
    }
    catch(err){
      console.log(err);
    }
    this.client = null;
    this.subscriptions = [];
  }
 } */

 testEmit(channel : string){
this.messageListener.next({
         channel,
         message: "{}"
       })
 }

}
